import { API } from '@/config/api';
import { useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { MessageSquare, X, Send, Sparkles, Bot, User, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// Premium markdown components for world-class formatting
const MarkdownComponents = {
  h1: ({ children }) => (
    <div className="flex items-center gap-3 mb-4 mt-6 pb-3 border-b-2 border-indigo-200">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
        <Info className="w-5 h-5 text-white" />
      </div>
      <h1 className="text-xl font-bold text-slate-900">{children}</h1>
    </div>
  ),
  h2: ({ children }) => (
    <div className="flex items-center gap-2 mb-3 mt-5">
      <div className="w-1.5 h-6 rounded-full bg-gradient-to-b from-indigo-500 to-purple-600"></div>
      <h2 className="text-lg font-bold text-slate-800">{children}</h2>
    </div>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-indigo-700 mb-2 mt-4 flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="text-slate-700 mb-3 leading-relaxed text-[15px]">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="space-y-2 mb-4 ml-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="space-y-2 mb-4 ml-1 list-none counter-reset-item">{children}</ol>
  ),
  li: ({ children, ordered }) => (
    <li className="flex items-start gap-3 text-slate-700 text-[15px]">
      <span className="mt-1.5 w-2 h-2 rounded-full bg-gradient-to-r from-indigo-400 to-purple-500 flex-shrink-0"></span>
      <span className="leading-relaxed">{children}</span>
    </li>
  ),
  strong: ({ children }) => {
    // Check if it's a number/data point
    const text = String(children);
    const isNumber = /[\d,]+\s*(celler|%|mill)/.test(text);
    const isStation = /^[A-L]\s*-/.test(text);
    
    if (isNumber) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-800 font-bold text-sm">
          {children}
        </span>
      );
    }
    if (isStation) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 font-semibold text-sm">
          {children}
        </span>
      );
    }
    return <strong className="font-semibold text-slate-900">{children}</strong>;
  },
  em: ({ children }) => (
    <em className="text-slate-600 not-italic bg-slate-100 px-1 rounded">{children}</em>
  ),
  code: ({ children }) => (
    <code className="bg-slate-100 text-purple-700 px-2 py-1 rounded-md text-sm font-mono border border-slate-200">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl overflow-x-auto my-4 text-sm shadow-lg">{children}</pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="relative pl-6 pr-4 py-3 my-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-l-4 border-amber-400 shadow-sm">
      <AlertTriangle className="absolute left-2 top-3 w-4 h-4 text-amber-500" />
      <div className="text-slate-700 italic">{children}</div>
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-xl border border-slate-200 shadow-sm">
      <table className="min-w-full divide-y divide-slate-200">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gradient-to-r from-slate-100 to-slate-50">{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-slate-100 bg-white">{children}</tbody>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-slate-50 transition-colors">{children}</tr>
  ),
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 text-sm text-slate-700">{children}</td>
  ),
  hr: () => (
    <div className="my-6 flex items-center gap-4">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
      <Sparkles className="w-4 h-4 text-slate-400" />
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
    </div>
  ),
};

// Section wrapper for organized content
const SectionCard = ({ title, icon: Icon, children, color = "indigo" }) => {
  const colors = {
    indigo: "from-indigo-500 to-purple-600",
    emerald: "from-emerald-500 to-teal-600",
    amber: "from-amber-500 to-orange-600",
    rose: "from-rose-500 to-pink-600",
  };
  
  return (
    <div className="mb-4 rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${colors[color]} text-white`}>
        {Icon && <Icon className="w-4 h-4" />}
        <span className="font-semibold text-sm">{title}</span>
      </div>
      <div className="p-4 bg-white">
        {children}
      </div>
    </div>
  );
};

export default function ChatModal({
  isOpen,
  onClose,
  chatMessages,
  setChatMessages,
  chatInput,
  setChatInput,
  chatLoading,
  setChatLoading,
  sessionId
}) {
  const messagesEndRef = useRef(null);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const message = chatInput.trim();
    setChatInput('');
    setChatLoading(true);
    setChatMessages(prev => [...prev, { role: 'user', content: message }]);
    
    try {
      const res = await axios.post(`${API}/chat-assistant`, {
        message: message,
        session_id: sessionId
      }, { withCredentials: true });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ **Beklager**, eg fekk ikkje til å svare akkurat no. Prøv igjen om litt.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleQuickQuestion = async (message) => {
    setChatInput('');
    setChatLoading(true);
    setChatMessages(prev => [...prev, { role: 'user', content: message }]);
    try {
      const res = await axios.post(`${API}/chat-assistant`, {
        message: message,
        session_id: sessionId
      }, { withCredentials: true });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ **Beklager**, eg fekk ikkje til å svare akkurat no. Prøv igjen om litt.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };
  
  const clearChat = () => {
    setChatMessages([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Premium Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-white/30">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">Microsafe AI</h2>
              <p className="text-sm text-white/80">Senior marinbiolog • Ekspertanalyse</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {chatMessages.length > 0 && (
              <button
                onClick={clearChat}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/20 transition-all"
                title="Ny samtale"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/20 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-b from-slate-50 to-white">
          {chatMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mb-6 shadow-xl shadow-purple-500/25 ring-4 ring-white">
                <Bot className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Hei! 👋</h3>
              <p className="text-slate-600 max-w-sm mb-8">
                Eg har tilgang til alle prøvedata og gjev deg ekspertanalyse på sekundet.
              </p>
              
              {/* Quick action cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full max-w-2xl">
                <button
                  onClick={() => handleQuickQuestion('Gi meg ein fullstendig analyse av Skeletonema-utviklinga gjennom heile 2025, inkludert alle stasjonar.')}
                  className="group p-4 bg-white border-2 border-slate-200 rounded-2xl text-left hover:border-indigo-400 hover:shadow-lg hover:shadow-indigo-500/10 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center mb-3 group-hover:bg-indigo-500 transition-colors">
                    <TrendingUp className="w-5 h-5 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <p className="font-semibold text-slate-900 text-sm">Skeletonema 2025</p>
                  <p className="text-xs text-slate-500 mt-1">Heilårsanalyse</p>
                </button>
                <button
                  onClick={() => handleQuickQuestion('Kva er dei viktigaste terskelverdiane og risikonivåa for algane i systemet?')}
                  className="group p-4 bg-white border-2 border-slate-200 rounded-2xl text-left hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3 group-hover:bg-amber-500 transition-colors">
                    <AlertTriangle className="w-5 h-5 text-amber-600 group-hover:text-white transition-colors" />
                  </div>
                  <p className="font-semibold text-slate-900 text-sm">Terskelverdiar</p>
                  <p className="text-xs text-slate-500 mt-1">Risiko og grenser</p>
                </button>
                <button
                  onClick={() => handleQuickQuestion('Oppsummer dei siste prøveregistreringane og peik på eventuelle bekymringar.')}
                  className="group p-4 bg-white border-2 border-slate-200 rounded-2xl text-left hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3 group-hover:bg-emerald-500 transition-colors">
                    <CheckCircle className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <p className="font-semibold text-slate-900 text-sm">Siste prøver</p>
                  <p className="text-xs text-slate-500 mt-1">Status og funn</p>
                </button>
              </div>
            </div>
          ) : (
            <>
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/20 ring-2 ring-white">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-5 py-3 shadow-lg shadow-purple-500/20'
                        : 'bg-white border border-slate-200 px-6 py-5 shadow-sm'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <p className="text-[15px] leading-relaxed">{msg.content}</p>
                    ) : (
                      <div className="prose prose-slate prose-sm max-w-none">
                        <ReactMarkdown components={MarkdownComponents}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0 ring-2 ring-white">
                      <User className="w-5 h-5 text-slate-600" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
          
          {/* Premium Loading */}
          {chatLoading && (
            <div className="flex gap-4 justify-start">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/20 ring-2 ring-white">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl px-6 py-5 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-3 h-3 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-sm text-slate-500 font-medium">Analyserer data...</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Input Area */}
        <div className="px-6 py-5 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Still eit spørsmål om algedata..."
                disabled={chatLoading}
                className="w-full pl-4 pr-4 py-3 text-base border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all bg-slate-50"
              />
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={!chatInput.trim() || chatLoading}
              className="h-12 w-12 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Sparkles className="w-3 h-3 text-slate-400" />
            <p className="text-xs text-slate-400">
              AI med tilgang til alle prøvedata • Trykk Enter for å sende
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
